import React, { useCallback, useEffect, useState } from "react";
import { Download, Receipt } from "lucide-react";
import { toast } from "sonner";
import { apiClient, API } from "../lib/api";
import { getToken } from "../lib/auth";
import { fmtInr } from "../lib/format";

export default function FeesSection({ clientId, scope = "advisor", refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const baseUrl =
    scope === "advisor"
      ? `/advisor/clients/${clientId}/fees`
      : `/client/fees`;
  const invoiceBase =
    scope === "advisor"
      ? `/advisor/clients/${clientId}/invoices`
      : `/client/invoices`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(baseUrl);
      setData(data);
    } catch (err) {
      // Silent: client may not have fees configured
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const downloadInvoice = async (periodKey) => {
    try {
      const res = await fetch(`${API}${invoiceBase}/${periodKey}.pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice_${periodKey}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download invoice");
    }
  };

  if (loading) return null;

  if (!data || !data.periods || data.periods.length === 0) {
    if (scope === "advisor") {
      return (
        <section className="mb-8">
          <h2 className="text-xl font-serif font-medium tracking-tight flex items-center gap-2 mb-3">
            <Receipt className="h-4 w-4" strokeWidth={1.5} />
            Advisory fees
          </h2>
          <div className="border border-subtle bg-card p-6 text-sm text-muted-foreground">
            No fee schedule set for this client. Add an{" "}
            <span className="font-medium text-foreground">Annual advisory fee (%)</span>{" "}
            via Edit client to generate H1 / H2 invoices automatically.
          </div>
        </section>
      );
    }
    return null;
  }

  return (
    <section className="mb-8" data-testid="fees-section">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-xl font-serif font-medium tracking-tight flex items-center gap-2">
          <Receipt className="h-4 w-4" strokeWidth={1.5} />
          Advisory fees
        </h2>
        <div className="text-xs text-muted-foreground">
          Billed:{" "}
          <span className="font-mono text-foreground">{fmtInr(data.total_billed)}</span> ·
          Projected: <span className="font-mono">{fmtInr(data.projected)}</span>
        </div>
      </div>

      <div className="border border-subtle bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary border-b border-subtle">
            <tr className="text-[10px] uppercase-track text-muted-foreground">
              <th className="text-left p-3 font-medium">Period</th>
              <th className="text-left p-3 font-medium">Status</th>
              <th className="text-right p-3 font-medium">Rate</th>
              <th className="text-right p-3 font-medium">Capital</th>
              <th className="text-right p-3 font-medium">Fee (₹)</th>
              <th className="text-right p-3 font-medium">Invoice</th>
            </tr>
          </thead>
          <tbody>
            {data.periods.map((p) => (
              <tr key={p.key} className="border-b border-subtle hover:bg-secondary">
                <td className="p-3">
                  <div className="font-mono text-xs">{p.key}</div>
                  <div className="text-[11px] text-muted-foreground">{p.label}</div>
                </td>
                <td className="p-3">
                  <span
                    className={`text-[10px] px-2 py-0.5 font-semibold uppercase-track ${
                      p.status === "BILLED"
                        ? "bg-profit-soft"
                        : "bg-gold-soft"
                    }`}
                  >
                    {p.status}
                  </span>
                </td>
                <td className="p-3 text-right tabular font-mono text-xs">
                  {p.half_year_rate_pct.toFixed(3)}%
                </td>
                <td className="p-3 text-right tabular font-mono">{fmtInr(p.total_capital)}</td>
                <td className="p-3 text-right tabular font-mono font-medium">
                  {fmtInr(p.total_fee)}
                </td>
                <td className="p-3 text-right">
                  <button
                    data-testid={`invoice-download-${p.key}`}
                    onClick={() => downloadInvoice(p.key)}
                    className="border border-border hover:bg-secondary px-2 py-1 text-[10px] uppercase-track inline-flex items-center gap-1"
                  >
                    <Download className="h-3 w-3" strokeWidth={1.5} />
                    PDF
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
        Fee charged at <strong>{data.periods[0]?.annual_fee_pct}% per annum</strong>,
        split half-yearly (Apr–Sep and Oct–Mar). Mid-period capital additions are pro-rated
        for the days they were active.
      </p>
    </section>
  );
}
